import {Component, EventEmitter, Input, Optional, Output, ViewChild} from '@angular/core';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {NzUploadChangeParam, NzUploadFile, NzUploadXHRArgs} from 'ng-zorro-antd/upload';
import {
  catchError, concatMap,
  filter,
  finalize,
  forkJoin,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subscription,
  throwError
} from 'rxjs';
import {HttpClient, HttpEventType, HttpResponse} from '@angular/common/http';
import {NzNotificationRef, NzNotificationService} from 'ng-zorro-antd/notification';
import {
  Document,
  DocumentLinkCreate,
  DocumentValidationCreate, EntityType,
  Partner,
  PostResponse,
  ToastConfig,
  User
} from '@app/shared';
import {NzDrawerRef} from 'ng-zorro-antd/drawer';
import {DocumentService, ScreenService, compareById} from '@app/core';
import {tap} from 'rxjs/operators';
import {ProjectUtilsService} from '../../../features/project/services/project-utils.service';
import {AuthService} from '../../../core/services/auth.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GlobalValuesService} from '../../../core/services/global-values.service';


interface GeneralModalData {
  type: 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete';
  title: string;
  actionIcon: string;
  actionTitle: string;
}

export interface UploadFormData {
  documentType?: string;
  documentTypeId?: number;
  file: File;
  expirationDate: Date;
  progress?: number; // Porcentaje de subida (0 a 100)
  status: 'pending' | 'uploading' | 'success' | 'error';
  ownerId: number;
}


@Component({
  selector: 'app-doc-modal',
  standalone: false,

  templateUrl: './doc-modal.component.html',
  styleUrl: './doc-modal.component.less'
})
export class DocModalComponent {

  generalDatas: GeneralModalData[] = [
    {type: 'upload', title: 'Subir documentos', actionIcon: 'upload', actionTitle: 'Subir'},
    {type: 'correct', title: 'Corregir documento', actionIcon: 'upload', actionTitle: 'Corregir documento'},
    {type: 'validate', title: 'Validar documento', actionIcon: 'check_circle', actionTitle: 'Validar'},
    {
      type: 'return',
      title: 'Volver a pendiente de validación',
      actionIcon: '',
      actionTitle: 'Volver a pendiente de validación'
    },
    {type: 'reject', title: 'Rechazar documento', actionIcon: 'close', actionTitle: 'Rechazar'},
    {type: 'delete', title: 'Eliminar documento', actionIcon: 'check_circle', actionTitle: 'Eliminar'},
  ];
  currentGeneralData: GeneralModalData | undefined;
  // Variables de 'upload'
  @ViewChild('uploadComponent') uploadComponent: any;
  uploadForms: UploadFormData[] = [];

  // Variables del resto de tipos
  @Input() onlyReturnFiles: boolean = false;
  @Input() document: Document | undefined;
  @Input() documentsToDelete: Document[] | undefined;
  @Input() entityId: number = 0;
  @Input() entityType: EntityType = 'Project';
  @Input() documentType: 'MBL' | 'HBL' | undefined;
  rejectReason: string | undefined;

  @Output() deleteEvent: EventEmitter<Document[]> = new EventEmitter<Document[]>();
  @Output() createEvent: EventEmitter<Document[]> = new EventEmitter<Document[]>();
  @Output() proccessFilesEvent: EventEmitter<File[]> = new EventEmitter<File[]>();

  // Notifications variables
  @ViewChild('notificationTmpl') notificationTmpl: any;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Los archivos se han subido.',
    showUndo: false
  };

  notRef: NzNotificationRef | undefined;

  isMobile$;
  partnerOptions: Partner[] = [];
  documentTypes: {name: string, id: number}[] = [];

  compareById = compareById;

  loading: boolean = false;

  loggedUser: User | undefined;

  newDocuments: Document[] = [];

  constructor(@Optional() private modalRef: NzModalRef<DocModalComponent>,
              @Optional() public drawerRef: NzDrawerRef<DocModalComponent>,
              private http: HttpClient,
              private screenService: ScreenService,
              private notificationService: NzNotificationService,
              private documentService: DocumentService,
              private ps: ProjectUtilsService,
              public authService: AuthService,
              public globalValuesService:GlobalValuesService
  ) {
    this.isMobile$ = screenService.isMobile$;
    // this.partnerOptions = ps.partnersOptions;
    // this.documentTypes = ps.documentTypes;
    authService.loggedUser
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.loggedUser = value);
    this.globalValuesService.documentTypes$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value) {
          this.documentTypes = !!authService.loggedUser$.value?.uploadDocumentsLimited
            ? value.filter(dt => dt.id === 13 || dt.id === 30) : value;
        }
      });
    this.globalValuesService.isPartnerReady$.pipe(takeUntilDestroyed())
      .subscribe(value => {
        if(value){
          this.partnerOptions=this.globalValuesService.partnerlist;
        }
      })
  }

  private _docModalType: any;

  get docModalType(): 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete' {
    return this._docModalType;
  }

  @Input()
  set docModalType(value: 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete') {
    this.currentGeneralData = this.generalDatas.find(d => d.type === value);
    this._docModalType = value;
  }

  get allFormsValid(): boolean {
    return this.uploadForms.length > 0 && this.uploadForms
      .every(form => form.documentType && form.documentType.trim() !== '' && form.status === 'pending');
  }

  close(): void {
    this.modalRef?.close('closed');
    this.drawerRef?.close('closed');
  }

  // Este método se dispara cuando se selecciona uno o varios archivos
  handleUploadChange(info: NzUploadChangeParam): void {
    const defaultExpiration = new Date();
    defaultExpiration.setMonth(defaultExpiration.getMonth() + 1);

    if (this.document) {
      // Si this.document existe, se guarda únicamente el último archivo subido.
      const lastFile: NzUploadFile | undefined = info.fileList[info.fileList.length - 1];
      if (lastFile) {
        this.uploadForms = [{
          documentType: this.onlyReturnFiles ? 'MBL' : (this.document.type || this.documentType || ''),
          status: 'pending',
          expirationDate: defaultExpiration,
          file: lastFile.originFileObj as File,
          ownerId: this.loggedUser!.partnerId,
        }];
      }
    } else {
      // Caso normal: se recorre fileList y se evitan duplicados (sobrescribiendo el más viejo)
      this.uploadForms = info.fileList.reduce((acc: UploadFormData[], file: NzUploadFile) => {
        const newItem: UploadFormData = {
          documentType: this.onlyReturnFiles ? 'MBL' : (this.document?.type || this.documentType || ''),
          status: 'pending',
          expirationDate: defaultExpiration,
          file: file.originFileObj as File,
          ownerId: this.loggedUser!.partnerId,
        };
        // Si ya existe un archivo con el mismo nombre, lo reemplaza
        const index = acc.findIndex(item => item.file.name === file.name);
        if (index !== -1) {
          acc[index] = newItem;
        } else {
          acc.push(newItem);
        }
        return acc;
      }, []);
    }
  }

  deleteDocumentToUpload(form: UploadFormData): void {
    this.uploadForms = this.uploadForms.filter(item => item.file.name !== form.file.name);

    // Actualiza también la lista interna del nz-upload
    if (this.uploadComponent && this.uploadComponent.nzFileList) {
      this.uploadComponent.nzFileList = this.uploadComponent.nzFileList.filter(
        (f: NzUploadFile) => f.name !== form.file.name
      );
    }
  }

  startUpload(): void {
    const uploadObservables: Observable<any>[] = this.uploadForms.map((form: UploadFormData) => {
      // Inicializa estado y progreso
      form.status = 'uploading';
      form.progress = 0;

      const name = form.file.name.split('.')[0];

      const documentTypeId = this.documentTypes.find(dt => dt.name === form.documentType)!.id;

      const formData = new FormData();
      formData.append('file', form.file, form.file.name);
      formData.append('type', form.documentType!);
      formData.append('name', name);
      formData.append('documentTypeId', documentTypeId.toString());
      formData.append('expirationDate', form.expirationDate.toISOString());
      formData.append('ownerId', form.ownerId.toString());
      formData.append('partnerId', this.loggedUser!.partnerId.toString());

      // Retornamos el observable que se suscribe a los eventos de la petición
      return this.documentService.createDocument(formData).pipe(
        tap(event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            form.progress = Math.round(100 * event.loaded / event.total);
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        map(event => (event as HttpResponse<PostResponse>).body),
        catchError((error: any) => {
          form!.status = 'error';
          form!.progress = 100;
          return throwError(() => error);
        }),
        mergeMap((documentResponse: any) => {
          if (!documentResponse?.success) return of(documentResponse);

          const documentLinkPayload: DocumentLinkCreate = {
            documentId: documentResponse.data.id,
            innerLinkDocumentPayload: {
              isLinked: true,
              entityId: this.entityId
            },
            entityType: this.entityType,
          };

          return this.documentService.createDocumentLink(documentLinkPayload).pipe(
            map(linkResponse => ({
              ...linkResponse,
              linkSuccess: linkResponse.success
            })),
            catchError(linkError => {
              console.error('Error vinculando documento:', linkError);
              console.log('Error en Next');
              return of({
                ...documentResponse,
                linkSuccess: false
              });
            })
          );
        })
      );
    });

    // Usamos concatMap para garantizar que las solicitudes se realicen una por una
    from(uploadObservables).pipe(
      concatMap((obs) => obs)  // Procesa cada observable en orden secuencial
    ).subscribe({
      next: (response) => {
        // Si alguna respuesta no fue exitosa, marcar como error y salir
        const allSuccess = response?.success && response?.linkSuccess;

        if (!allSuccess) {
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content: 'Hay archivos que no se subieron o no se vincularon correctamente!',
            showUndo: false
          };
          console.log('Error en Next');
        } else {
          // Si todos los documentos fueron subidos y vinculados correctamente
          this.newDocuments.push(response?.data)
        }
      },
      error: (e) => {
        // Error en el proceso de carga
        console.error(e);
        console.log('Error en Error');
        if (this.newDocuments.length > 0) {
          this.toastConfig = {
            icon: 'warning',
            iconColor: '#CB2A2F',
            content: 'No se han podido subir todos los documentos!',
            msg: e.error.message,
            showUndo: false
          };
          this.createEvent.emit(this.newDocuments);
        } else {
          this.toastConfig = {
            icon: 'dangerous',
            iconColor: '#CB2A2F',
            content: 'Ha ocurrido un error subiendo los documentos!',
            msg: e.error.message,
            showUndo: false
          };
        }

        this.loading = false;
        this.notRef = this.notificationService
          .create("blank", '', this.notificationTmpl, {nzPlacement: "bottomRight"});
      },
      complete: () => {
        // Llamada a la notificación solo cuando toddo haya finalizado (ya sea éxito o error)
        this.showNotification();
        this.createEvent.emit(this.newDocuments);
      }
    });
  }




  showNotification(): void {
    this.loading = false;
    this.notRef = this.notificationService
      .create("blank", '', this.notificationTmpl, {nzPlacement: "bottomRight"});
    this.close();
  }

  onConfirm(): void {
    this.loading = true;
    const userId = this.loggedUser!.id;
    const partnerId = this.loggedUser!.partnerId;

    switch (this.docModalType) {
      case 'upload': {
        if (this.onlyReturnFiles) {
          const files: File[] = this.uploadForms.map(v => v.file);
          this.proccessFilesEvent.emit(files);
          this.close();
        } else {
          this.startUpload();
        }
        break;
      }
      case 'correct': {
        this.startUpload();
        // this.document!.validationStatus = 'ValidationPending';
        // this.document!.rejectionDate = undefined;
        // this.document!.rejectionReason = undefined;
        // this.document!.correctionDate = new Date().toDateString();
        break;
      }
      case 'validate': {
        const params: DocumentValidationCreate = {
          documentId: this.document!.id,
          partnerId: partnerId,
          validationStatus: 'Validated'
        }
        this.documentService.createDocumentValidation(params)
          .pipe(finalize(() => this.showNotification()))
          .subscribe({
          next: () => {
            this.toastConfig.content = 'Documento validado.';
            this.document!.validationStatus = 'Validated';
            this.toastConfig.icon = 'check_circle';
            this.toastConfig.iconColor = '#6CDA75';
            delete this.toastConfig.msg; // el detalle de un error previo no acompania a un exito
            this.toastConfig.content = 'Documento validado.';
            // this.document!.validationDate = new Date().toDateString();
          },
          error: (e) => {
            this.toastConfig.icon = 'dangerous';
            this.toastConfig.iconColor = '#CB2A2F';
            this.toastConfig.content = 'Ha ocurrido un error validando el documento.';
            this.toastConfig.msg = e.error.message;
            console.error(e);
          }
        });
        break;
      }
      case 'delete': {
        const idToDelete = this.documentsToDelete?.map(d => d.id);
        if (idToDelete) {
          this.documentService.deleteDocument(idToDelete).subscribe();
        }
        this.deleteEvent.emit(this.documentsToDelete);
        this.toastConfig.content = 'Los archivos se han eliminado.';
        this.notRef = this.notificationService
          .create("blank", '', this.notificationTmpl, {nzPlacement: "bottomRight"});
        this.close();
        break;
      }
      case 'return': {
        const params: DocumentValidationCreate = {
          documentId: this.document!.id,
          partnerId: partnerId,
          validationStatus: 'ValidationPending',
          reason: this.rejectReason,
        }
        this.documentService.createDocumentValidation(params)
          .pipe(finalize(() => this.showNotification()))
          .subscribe({
            next: (rsp) => {
              this.toastConfig.content = 'Documento regresado a pendiente.';
              this.document!.validationStatus = 'ValidationPending';
              this.toastConfig.icon = 'check_circle';
              this.toastConfig.iconColor = '#6CDA75';
              delete this.toastConfig.msg; // el detalle de un error previo no acompania a un exito
              this.toastConfig.content = 'Documento vuelto a pendiente.';
              if (!this.document!.documentValidations) {
                this.document!.documentValidations = [];
              }
              this.document!.documentValidations.push(rsp.data)
            },
            error: (e) => {
              this.toastConfig.icon = 'dangerous';
              this.toastConfig.iconColor = '#CB2A2F';
              this.toastConfig.content = 'Ha ocurrido un error validando el documento.';
              this.toastConfig.msg = e.error.message;
              console.error(e);
            }
          });
        break;
      }
      case 'reject': {
        const params: DocumentValidationCreate = {
          documentId: this.document!.id,
          partnerId: partnerId,
          validationStatus: 'NonValidated',
          reason: this.rejectReason,
        }
        this.documentService.createDocumentValidation(params)
          .pipe(finalize(() => this.showNotification()))
          .subscribe({
            next: (rsp) => {
              this.toastConfig.content = 'Documento rechazado.';
              this.document!.validationStatus = 'NonValidated';
              this.toastConfig.icon = 'check_circle';
              this.toastConfig.iconColor = '#6CDA75';
              delete this.toastConfig.msg; // el detalle de un error previo no acompania a un exito
              this.toastConfig.content = 'Documento rechazado.';
              if (!this.document!.documentValidations) {
                this.document!.documentValidations = [];
              }
              this.document!.documentValidations.push(rsp.data)
            },
            error: (e) => {
              this.toastConfig.icon = 'dangerous';
              this.toastConfig.iconColor = '#CB2A2F';
              this.toastConfig.content = 'Ha ocurrido un error rechazando el documento.';
              this.toastConfig.msg = e.error.message;
              console.error(e);
            }
          });

        break;
      }
    }
  }


  // Metodo para prevenir el error en el navegador de una peticion erronea del nz-upload
  customUpload = (item: NzUploadXHRArgs): Subscription => {
    // Simula un retardo (opcional)
    setTimeout(() => {
      // Llama al callback de éxito para que nz-upload marque el archivo como subido
      item.onSuccess!({}, item.file, null);
    }, 0);
    // Retorna una suscripción vacía, ya que no se está haciendo una petición real
    return new Subscription();
  };


}
